import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import React, {
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSettings } from '../settings';
import { ALL_SCOPES } from './auth0';
import { AuthInternalProvider, CoreAuthContext } from './auth.context';

export const LogrocketIdentify: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const auth = useAuth();
  useEffect(() => {
    try {
      if (auth.user) {
        const lastUser = sessionStorage.getItem('logrocket-user');
        if (lastUser !== auth.user.email && auth.user.email) {
          sessionStorage.setItem('logrocket-user', auth.user.email);
          // LogRocket may be loaded as an external script
          const LogRocket = (window as unknown as { LogRocket?: { identify: (email: string, data: object) => void } }).LogRocket;
          if (LogRocket) {
            LogRocket.identify(auth.user.email, {
              name: auth.user.name,
              email: auth.user.email,
              ...auth.user,
            });
          }
          
          
        }
      }
    } catch {}
  }, [auth.user]);
  return <>{children}</>;
};

const AUTH_REDIRECT_KEY = 'auth0_redirect_ts';
const AUTH_LOGIN_REQUIRED_KEY = 'auth0_login_required';
const REDIRECT_COOLDOWN_MS = 5000; // 5 seconds

// Check if we just came back from Auth0 (has code or error in URL)
const hasAuth0Callback = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error') || params.has('state');
};

// Check if we recently redirected
const recentlyRedirected = () => {
  const ts = sessionStorage.getItem(AUTH_REDIRECT_KEY);
  if (!ts) return false;
  return (Date.now() - parseInt(ts, 10)) < REDIRECT_COOLDOWN_MS;
};

/**
 * Headless PDF render: backend mints a short-lived HS256 JWT and navigates Puppeteer to
 * `/.../document-preview?print=1&token=...`. Without this short-circuit, Auth0 auto-redirect
 * fires before React Router can mount the print provider, sending headless Chrome to the
 * Auth0 hosted login page and losing the original URL. Detection runs synchronously at module
 * scope so it survives the very first render of `Auth0Provider`.
 */
export const isHeadlessPrintRoute = (loc: Location | URL = window.location): boolean => {
  try {
    const params = new URLSearchParams(loc.search);
    return (
      params.get('print') === '1' &&
      typeof params.get('token') === 'string' &&
      (params.get('token') as string).length > 0 &&
      loc.pathname.includes('/document-preview')
    );
  } catch {
    return false;
  }
};

const AuthAutoLogin: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const auth = useAuth0();
  const [showLoginButton, setShowLoginButton] = useState(() => {
    return sessionStorage.getItem(AUTH_LOGIN_REQUIRED_KEY) === 'true';
  });

  useEffect(() => {
    /** Headless PDF render — never redirect, never gate. The route consumes a URL JWT directly. */
    if (isHeadlessPrintRoute()) {
      return;
    }
    if (sessionStorage.getItem(AUTH_LOGIN_REQUIRED_KEY) === 'true') {
      setShowLoginButton(true);
      return;
    }
    
    if (auth.isLoading) {
      return;
    }
    
    if (auth.isAuthenticated) {
      sessionStorage.removeItem(AUTH_REDIRECT_KEY);
      sessionStorage.removeItem(AUTH_LOGIN_REQUIRED_KEY);
      setShowLoginButton(false);
      return;
    }
    
    if (hasAuth0Callback()) {
      return;
    }
    
    if (recentlyRedirected()) {
      setShowLoginButton(true);
      return;
    }
    
    sessionStorage.setItem(AUTH_REDIRECT_KEY, Date.now().toString());
    auth.loginWithRedirect();
  }, [auth.isLoading, auth.isAuthenticated]);

  if (isHeadlessPrintRoute()) {
    return <>{children}</>;
  }
  
  if (showLoginButton) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        maxWidth: '400px',
        margin: '100px auto'
      }}>
        <h2 style={{ marginBottom: '16px' }}>Authentication Required</h2>
        <p style={{ marginBottom: '24px', color: '#666' }}>
          Automatic login failed. This can happen in incognito mode or with strict privacy settings.
        </p>
        <button 
          onClick={() => {
            // Clear all Auth0 state to get a fresh start
            sessionStorage.removeItem(AUTH_REDIRECT_KEY);
            sessionStorage.removeItem(AUTH_LOGIN_REQUIRED_KEY);
            // Also clear Auth0's internal state that might be corrupted
            Object.keys(localStorage).filter(k => k.startsWith('@@auth0')).forEach(k => localStorage.removeItem(k));
            auth.loginWithRedirect();
          }}
          style={{ 
            padding: '12px 24px', 
            fontSize: '16px', 
            cursor: 'pointer',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          Click here to log in
        </button>
      </div>
    );
  }
  
  return <>{children}</>;
};

const SETUP_PATH = '/profile/setup';

const RedirectIfNotSetup: FunctionComponent<PropsWithChildren<{
  disableProfileRedirect?: boolean;
}>> = ({ children, disableProfileRedirect }) => {
  const auth = useAuth();
  useEffect(() => {
    /** Headless PDF render must never trigger a profile-setup redirect. */
    if (isHeadlessPrintRoute()) return;
    if (
      !disableProfileRedirect &&
      auth.user &&
      !auth.user['https://core-backend.fence-estimator/app_metadata']
        ?.organization_id &&
      window.location.pathname.substring(0, SETUP_PATH.length) !== SETUP_PATH
    ) {
      window.location.href = SETUP_PATH;
    }
  }, [auth.user, disableProfileRedirect]);

  return <>{children}</>;
};

// Clean up stale Auth0 state from different origins to prevent redirect loops
const cleanupStaleAuth0State = () => {
  const currentOrigin = window.location.origin;
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('@@auth0')) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            // Check if stored state contains a different origin
            const parsed = JSON.parse(value);
            const storedUri = parsed?.body?.redirect_uri || parsed?.redirect_uri;
            if (storedUri && !storedUri.startsWith(currentOrigin)) {
              console.log('[Auth0] Clearing stale state for different origin:', key);
              localStorage.removeItem(key);
            }
          }
        } catch {
          // If we can't parse it, leave it alone
        }
      }
    });
  } catch (e) {
    console.warn('[Auth0] Error cleaning up stale state:', e);
  }
};

export const AuthProvider: FunctionComponent<PropsWithChildren<{
  disableProfileRedirect?: boolean;
}>> = ({ children, disableProfileRedirect }) => {
  const { getSetting } = useSettings();
  const domain = getSetting('AUTH_DOMAIN');
  const clientId = getSetting('AUTH_CLIENT_ID');
  const audience = 'https://core-backend.fence-estimator';

  // Clean up any stale Auth0 state from different origins (e.g., production vs dev)
  React.useEffect(() => {
    cleanupStaleAuth0State();
  }, []);

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      authorizationParams={{
        audience,
        scope: ALL_SCOPES.join(' '),
        redirect_uri: window.location.origin,
      }}>
      <AuthAutoLogin>
        <AuthInternalProvider>
          <LogrocketIdentify />
          <RedirectIfNotSetup disableProfileRedirect={disableProfileRedirect}>{children}</RedirectIfNotSetup>
        </AuthInternalProvider>
      </AuthAutoLogin>
    </Auth0Provider>
  );
};
/** While Auth0 / token claims are loading, returns `undefined`. Otherwise a definite boolean (missing claim → deny). */
export const useHasPermission = (scope: string): boolean | undefined => {
  const { permissions, isLoading: authLoading } = useAuth();
  const { isLoading: auth0Loading } = useAuth0();
  if (authLoading || auth0Loading) {
    return undefined;
  }
  if (!scope) {
    return true;
  }
  const list = Array.isArray(permissions) ? permissions : [];
  return list.includes(scope);
};

/** True if the user has at least one of the given permission strings (OR). */
export const useHasAnyPermission = (scopes: string[]): boolean | undefined => {
  const { permissions, isLoading: authLoading } = useAuth();
  const { isLoading: auth0Loading } = useAuth0();
  if (authLoading || auth0Loading) {
    return undefined;
  }
  if (!scopes.length) {
    return true;
  }
  const list = Array.isArray(permissions) ? permissions : [];
  return scopes.some((s) => list.includes(s));
};

export const useAuth = () => {
  const auth = useContext(CoreAuthContext);

  return auth;
};

export const useAuthPermissions = () => {
  const { permissions } = useAuth();
  return permissions;
};
